import { useEffect, useMemo, useRef } from 'react';

import { Button, Flex, Stack, Text, useDisclosure } from '@chakra-ui/react';

import { useNavigate } from 'react-router-dom';

import { ArrowLeftOutline } from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import Step from 'app/components/UI/Step';
import {
  ConfirmData,
  SelectCompany,
  SelectFileType,
  SelectPayCalendar,
  UploadFile,
} from 'app/components/Employees/ImportEmployees';
import { ImportEmployeesStep } from 'app/components/Employees/ImportEmployees/types';

import useFetchCompanies from 'app/hooks/useFetchCompanies';
import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchPayCalendars from 'app/hooks/useFetchPayCalendars';
import { useCancelAETFile } from 'app/hooks/useAETFileHooks';
import { useCancelEDFFile } from 'app/hooks/useEDFFileHooks';
import useStep from 'app/hooks/useStep';
import useCheckNonSalariedWorkers from 'app/hooks/useCheckSalariedWorkers';

import { FAILED_UPLOAD_STATUSES, FileUploadStatus } from 'app/constants/fileUploads';

import { useManagingCompanyStore, useImportEmployeesStore, useNavigationStore } from 'app/store';

import { getEmployeesPath } from 'app/utils/routes';

import { toHumanReadable, toKebabCase } from 'app/utils/strings';
import { ASSIGNABLE_PAY_CALENDAR_STATUSES } from 'app/utils/payCalendars';

function ImportEmployees() {
  const {
    isOpen: isLeaveWithoutImportOpen,
    onOpen: onOpenLeaveWithoutImport,
    onClose: onCloseLeaveWithoutImport,
  } = useDisclosure();

  const { view } = useNavigationStore.getState();

  const { company } = useManagingCompanyStore.getState();

  const navigate = useNavigate();

  const submitButtonRef = useRef<HTMLDivElement>(null);

  const importEmployeesData = useImportEmployeesStore((state) => state.importEmployeesData);
  const setImportEmployeesData = useImportEmployeesStore((state) => state.setImportEmployeesData);
  const resetImportEmployeesData = useImportEmployeesStore(
    (state) => state.resetImportEmployeesData
  );

  // Determine if we need to show company selection based on navigation context
  const needsCompanySelection = view === 'global' || !company?.id;

  const { data: companies, isInitialLoading: isLoadingCompanies } = useFetchCompanies(
    {
      page: 0,
      size: 999999,
      sortBy: 'name',
      sort: 'ASC',
    },
    needsCompanySelection
  );

  const { data: payCalendars, isInitialLoading: isLoadingPayCalendars } = useFetchPayCalendars(
    {
      companyId: [importEmployeesData.companyId || company?.id || ''],
      status: ASSIGNABLE_PAY_CALENDAR_STATUSES,
      page: 0,
      size: 999999,
    },
    // Fetch pay calendars when we have a company ID, regardless of view context
    !!(importEmployeesData.companyId || company?.id)
  );

  // Check for non-salaried workers in the selected company
  const companyIdForNonSalariedCheck = importEmployeesData.companyId || company?.id;
  // In global view, check non-salaried workers after company is selected
  // In company view, check non-salaried workers immediately
  const shouldCheckNonSalariedWorkers =
    (!needsCompanySelection && !!companyIdForNonSalariedCheck) || // Company view or global view after company selection
    (needsCompanySelection && !!importEmployeesData.companyId); // Global view after company selection but before step completion

  const { data: hasNonSalariedWorkers, isLoading: isLoadingNonSalariedCheckRaw } =
    useCheckNonSalariedWorkers(
      shouldCheckNonSalariedWorkers ? companyIdForNonSalariedCheck : undefined
    );

  // Override the loading state when we don't need the hook
  const isLoadingNonSalariedCheck = shouldCheckNonSalariedWorkers
    ? isLoadingNonSalariedCheckRaw
    : false;

  // Gifting-only companies only ever have one real EDF file type — resolve it directly from the
  // company record rather than the generic "select file type" step, which has no concept of
  // gifting and would otherwise let a non-salaried gifting company mis-select plain
  // EMPLOYEE_DATA_FILE (or offer AET, which gifting companies don't use).
  const { data: selectedCompany } = useFetchCompany(companyIdForNonSalariedCheck);
  const isGiftingOnlyCompany = (company?.type || selectedCompany?.type) === 'GIFTING_ONLY';

  const steps: ImportEmployeesStep[] = useMemo(() => {
    const stepList: ImportEmployeesStep[] = [
      ...(needsCompanySelection ? ['selectCompany' as const] : []),
      ...(hasNonSalariedWorkers === true && !isGiftingOnlyCompany
        ? ['selectFileType' as const]
        : []),
      // For EDF files, show pay calendar step only when there are multiple pay calendars
      // (single pay calendar is auto-selected via useEffect below)
      ...(importEmployeesData.fileType === 'EMPLOYEE_DATA_FILE' &&
      (!!importEmployeesData.companyId || !!company?.id) &&
      !isLoadingPayCalendars &&
      (payCalendars?.list.length || 0) > 1
        ? ['selectPayCalendar' as const]
        : []),
      'uploadFile' as const,
      'confirmData' as const,
    ];

    return stepList;
  }, [
    needsCompanySelection,
    hasNonSalariedWorkers,
    isGiftingOnlyCompany,
    importEmployeesData.fileType,
    importEmployeesData.companyId,
    company?.id,
    payCalendars?.list,
    isLoadingPayCalendars,
  ]);

  const { currentStep, nextStep, previousStep, canPrevStep } = useStep(steps.length);

  const isAET = importEmployeesData.fileType === 'APPROVED_EARNINGS_TIMESHEET';

  const { mutate: cancelAETFile, isLoading: isCancellingAETFile } = useCancelAETFile({
    onSuccess: () => previousStep(),
  });

  const { mutate: cancelEDFFile, isLoading: isCancellingEDFFile } = useCancelEDFFile({
    onSuccess: () => previousStep(),
  });

  const isCancellingUpload = isCancellingAETFile || isCancellingEDFFile;

  const handleCancel = () => {
    if (steps[currentStep] === 'confirmData') {
      onOpenLeaveWithoutImport();
      setImportEmployeesData({ leaveModalSource: 'cancel' });
    } else {
      navigate(getEmployeesPath());
    }
  };

  // bulkImportStatus is set from real fps statuses (see UploadFile) — 'READY'/'ERROR' are legacy
  // company-manager statuses that fps never emits, so these checks never actually fired for the
  // fps-backed AET/EDF flows.
  const hasFailedUpload = FAILED_UPLOAD_STATUSES.includes(
    importEmployeesData.bulkImportStatus as FileUploadStatus
  );

  const handleBack = () => {
    if (
      steps[currentStep] === 'confirmData' &&
      importEmployeesData.bulkImportStatus &&
      !hasFailedUpload
    ) {
      onOpenLeaveWithoutImport();
      setImportEmployeesData({ leaveModalSource: 'back' });
    } else if (
      importEmployeesData.bulkImportId &&
      steps[currentStep] === 'confirmData' &&
      hasFailedUpload
    ) {
      const cancellationRequest = { statusReason: 'User cancelled the upload process' };
      if (isAET) {
        cancelAETFile({ fileUploadId: importEmployeesData.bulkImportId, cancellationRequest });
      } else {
        cancelEDFFile({ fileUploadId: importEmployeesData.bulkImportId, cancellationRequest });
      }
    } else {
      // Check if we're going back to the upload step - if so, reset upload-related states
      const nextStepIndex = currentStep - 1;
      if (nextStepIndex >= 0 && steps[nextStepIndex] === 'uploadFile') {
        // Reset upload-related states when going back to upload step
        setImportEmployeesData({
          bulkImportId: '',
          bulkImportStatus: undefined,
          uploadedAETFile: undefined,
          uploadedEDFFile: undefined,
        });
      }
      previousStep();
    }
  };

  const renderCurrentStepForm = () => {
    const currentStepName = steps[currentStep];

    // Safety check to prevent undefined errors
    if (!currentStepName) {
      return <CenterSpinner />;
    }

    switch (currentStepName) {
      case 'confirmData':
        return (
          <ConfirmData
            id={toKebabCase(currentStepName)}
            onStepAction={previousStep}
            isLeaveWithoutImportOpen={isLeaveWithoutImportOpen}
            onCloseLeaveWithoutImport={onCloseLeaveWithoutImport}
            onBack={handleBack}
            fileType={importEmployeesData.fileType || 'EMPLOYEE_DATA_FILE'}
          />
        );
      case 'selectPayCalendar':
        return (
          <SelectPayCalendar
            id={toKebabCase(currentStepName)}
            ref={submitButtonRef}
            onStepAction={nextStep}
            onBack={previousStep}
            isLeaveWithoutImportOpen={isLeaveWithoutImportOpen}
            onCloseLeaveWithoutImport={onCloseLeaveWithoutImport}
            payCalendars={payCalendars?.list || []}
          />
        );
      case 'selectFileType':
        return (
          <SelectFileType
            id={toKebabCase(currentStepName)}
            ref={submitButtonRef}
            onStepAction={nextStep}
            onBack={previousStep}
            isLeaveWithoutImportOpen={isLeaveWithoutImportOpen}
            onCloseLeaveWithoutImport={onCloseLeaveWithoutImport}
          />
        );
      case 'selectCompany':
        return (
          <SelectCompany
            id={toKebabCase(currentStepName)}
            ref={submitButtonRef}
            onStepAction={nextStep}
            onBack={previousStep}
            isLeaveWithoutImportOpen={isLeaveWithoutImportOpen}
            onCloseLeaveWithoutImport={onCloseLeaveWithoutImport}
            companies={companies?.list || []}
          />
        );
      case 'uploadFile':
      default:
        return (
          <UploadFile
            id={toKebabCase(currentStepName)}
            onStepAction={nextStep}
            onBack={previousStep}
            isLeaveWithoutImportOpen={isLeaveWithoutImportOpen}
            onCloseLeaveWithoutImport={onCloseLeaveWithoutImport}
          />
        );
    }
  };

  useEffect(() => {
    if (company && !needsCompanySelection && company.id !== importEmployeesData.companyId) {
      // If we have company context (not from global view), set it in the store
      setImportEmployeesData({
        companyId: company.id,
        companyName: company.name,
      });
    }
  }, [company, needsCompanySelection, importEmployeesData.companyId, setImportEmployeesData]);

  useEffect(() => {
    if (payCalendars && payCalendars.list.length === 1) {
      setImportEmployeesData({
        payCalendarId: payCalendars.list[0].id,
        payCalendarName: payCalendars.list[0].name,
      });
    }
  }, [payCalendars, setImportEmployeesData, importEmployeesData.payCalendarId]);

  // Auto-set file type when there's no real choice to make: gifting-only companies always use
  // the gifting EDF type (they never see selectFileType, above), and non-gifting companies with
  // no non-salaried workers only have EDF anyway.
  useEffect(() => {
    if (
      (isGiftingOnlyCompany || hasNonSalariedWorkers === false) &&
      !importEmployeesData.fileType
    ) {
      setImportEmployeesData({
        fileType: isGiftingOnlyCompany ? 'GIFTING_ONLY_EMPLOYEE_DATA_FILE' : 'EMPLOYEE_DATA_FILE',
      });
    }
  }, [
    isGiftingOnlyCompany,
    hasNonSalariedWorkers,
    importEmployeesData.fileType,
    setImportEmployeesData,
  ]);

  useEffect(() => {
    return () => resetImportEmployeesData();
  }, [resetImportEmployeesData]);

  const getPageTitle = () => {
    if (importEmployeesData.fileType === 'APPROVED_EARNINGS_TIMESHEET') {
      return 'Upload timesheet';
    }
    if (importEmployeesData.fileType === 'EMPLOYEE_DATA_FILE') {
      return 'Upload employees';
    }
    return 'Upload data';
  };

  return (
    <AuthenticatedPageWrapper title={getPageTitle()}>
      <Flex flexDir="column" gap="25px" flexGrow={1}>
        <Stack spacing="10px">
          <Flex justify="space-between" gap="20px" alignItems="center">
            <Text as="h2" textStyle="h2" color="brand.darkPurple" mr="auto">
              {getPageTitle()}
            </Text>
            <Flex gap="12px" ref={submitButtonRef}>
              <Button
                mr="20px"
                size="sm"
                variant={steps.length === 2 && !canPrevStep ? 'outline' : 'link'}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              {canPrevStep && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<ArrowLeftOutline boxSize="20px" />}
                  onClick={handleBack}
                  isLoading={isCancellingUpload}
                >
                  Back
                </Button>
              )}
            </Flex>
          </Flex>

          {steps[currentStep] !== 'selectCompany' && (
            <Text as="h3" textStyle="h3" color="brand.mutedPurple">
              {importEmployeesData.companyName}
            </Text>
          )}
          {steps[currentStep] !== 'selectCompany' && steps[currentStep] !== 'selectPayCalendar' && (
            <Text as="h4" textStyle="h4Bold" color="brand.purple">
              {importEmployeesData.payCalendarName}
            </Text>
          )}
        </Stack>
        {isLoadingCompanies || isLoadingPayCalendars || isLoadingNonSalariedCheck ? (
          <CenterSpinner />
        ) : (
          <Flex flexDir="column" gap="50px" height="100%" flexGrow={1}>
            <Flex
              w="full"
              maxW={
                !company?.id ||
                (payCalendars?.list.length || 0) > 1 ||
                payCalendars?.list.length === 0
                  ? '370px'
                  : '200px'
              }
              alignSelf="center"
            >
              {steps.map((step, id) => (
                <Step
                  key={step}
                  number={id + 1}
                  name={toHumanReadable(step)}
                  isActive={currentStep === id}
                  isCompleted={currentStep > id}
                  isFirstStep={id === 0}
                  isLastStep={steps.length === id + 1}
                />
              ))}
            </Flex>
            {renderCurrentStepForm()}
          </Flex>
        )}
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default ImportEmployees;
